/**
 * Accueil du portail étudiant.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Lock, Unlock, FileText,
  Check, X, ChevronRight, Bell,
  Upload, Inbox, Camera, Trash2, Newspaper, Settings, ShieldAlert, Cake,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi, MeResponse } from '../services/portailApi';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { SoumettreRecuModal } from '../components/SoumettreRecuModal';
import { AnnonceParcoursModal } from '../components/AnnonceParcoursModal';

const PERIODE_LABELS: Record<string, string> = {
  INSC: 'Inscription / Réinscription',
  NOV: 'Novembre',
  DEC: 'Décembre',
  JAN: 'Janvier',
  FEV: 'Février',
  MAR: 'Mars',
  AVR: 'Avril',
  MAI: 'Mai',
  JUIN: 'Juin',
  JUIL: 'Juillet',
  AOUT: 'Août',
  EXAM: 'Examen',
};

function formatMoney(s: string): string {
  const n = parseFloat(s);
  if (isNaN(n) || n === 0) return '0 FCFA';
  return new Intl.NumberFormat('fr-FR').format(n) + ' FCFA';
}

const PUSH_DISMISS_KEY = 'portail_push_banner_dismissed';
// Annonce « résultats des années antérieures » — affichée une seule fois par appareil.
const ANNONCE_PARCOURS_KEY = 'portail_annonce_parcours_vue_v1';

export default function Accueil() {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const push = usePushNotifications();
  const [bannerHidden, setBannerHidden] = useState(
    () => localStorage.getItem(PUSH_DISMISS_KEY) === '1'
  );
  const [showSoumettreRecu, setShowSoumettreRecu] = useState(false);
  const [soumissionOk, setSoumissionOk] = useState(false);
  const [nbActuNonLues, setNbActuNonLues] = useState(0);
  const [annivJour, setAnnivJour] = useState<any[]>([]);
  const [showAnnonceParcours, setShowAnnonceParcours] = useState(false);

  // Annonce une seule fois aux étudiants ayant au moins une année ANTÉRIEURE avec
  // résultats : « vos résultats des années précédentes sont disponibles ».
  useEffect(() => {
    let mounted = true;
    if (localStorage.getItem(ANNONCE_PARCOURS_KEY) === '1') return;
    portailApi.annees()
      .then((r) => {
        const eligible = (r.annees || []).some((a) => !a.courante && a.a_resultats);
        if (mounted && eligible) setShowAnnonceParcours(true);
      })
      .catch(() => { /* silencieux */ });
    return () => { mounted = false; };
  }, []);

  const fermerAnnonceParcours = () => {
    try { localStorage.setItem(ANNONCE_PARCOURS_KEY, '1'); } catch { /* ignore */ }
    setShowAnnonceParcours(false);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await portailApi.me();
        if (mounted) setMe(res);
      } catch (e: any) {
        if (e.status === 401) {
          portailApi.clearToken();
          navigate('/portail/login', { replace: true });
        } else {
          if (mounted) setError(e.message || 'Erreur de chargement');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [navigate]);

  // Fix 2026-09-07 : rafraîchir badges (notifs + actualités) au retour sur
  // cette page — visibility, focus, et rechargement direct de `me`. Sans
  // ça, cliquer sur Notifications → lire → revenir à l'accueil laissait
  // le badge rouge en place jusqu'au prochain rechargement complet.
  const refreshBadges = React.useCallback(async () => {
    try {
      const [res, actu] = await Promise.all([
        portailApi.me().catch(() => null),
        portailApi.actualitesList().catch(() => null),
      ]);
      if (res) setMe(res);
      if (actu) setNbActuNonLues(actu.nb_non_lus || 0);
    } catch { /* silencieux */ }
  }, []);

  useEffect(() => {
    let mounted = true;
    portailApi.actualitesList()
      .then((r) => { if (mounted) setNbActuNonLues(r.nb_non_lus || 0); })
      .catch(() => { /* silencieux */ });
    portailApi.anniversairesDuJour()
      .then((r) => { if (mounted) setAnnivJour(r.results || []); })
      .catch(() => { /* silencieux */ });
    const onVisible = () => { if (document.visibilityState === 'visible') refreshBadges(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refreshBadges);
    return () => {
      mounted = false;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refreshBadges);
    };
  }, [refreshBadges]);

  const logout = () => {
    portailApi.clearToken();
    navigate('/portail/login', { replace: true });
  };

  if (loading) {
    return (
      <PortailLayout showHeader>
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
          <p className="text-gray-400 mt-4 text-sm">Chargement...</p>
        </div>
      </PortailLayout>
    );
  }

  if (error || !me) {
    return (
      <PortailLayout showHeader>
        <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 mt-8">
          <p className="text-red-300 text-sm">{error}</p>
          <button onClick={logout} className="mt-3 text-sm underline text-blue-400">Se déconnecter</button>
        </div>
      </PortailLayout>
    );
  }

  const etu = me.etudiant;
  const acces = me.acces_resultats;
  const statutPaiements = me.statut_paiements || [];

  return (
    <PortailLayout showHeader title={`Bonjour ${etu.prenoms.split(' ')[0]}`}>
      <div className="space-y-3">
        {/* Carte profil avec upload photo */}
        <ProfilCard etu={etu} onPhotoChanged={(url) => setMe(me ? { ...me, etudiant: { ...me.etudiant, photo_url: url } } : me)} />

        {/* Bannière anniversaire — affichée s'il y a des fêteurs aujourd'hui */}
        {annivJour.length > 0 && (
          <button
            onClick={() => navigate('/portail/anniversaires')}
            className="w-full text-left bg-gray-900 border border-gray-800 hover:border-amber-600 rounded-xl p-3 transition-colors flex items-center gap-3"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-900/30 border border-amber-700/60 flex items-center justify-center">
              <Cake size={18} className="text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-gray-100">
                {annivJour.filter((a: any) => !a.est_moi).length > 0
                  ? `${annivJour.filter((a: any) => !a.est_moi).length} étudiant${annivJour.length > 1 ? 's' : ''} fête${annivJour.length > 1 ? 'nt' : ''} aujourd'hui`
                  : "C'est votre anniversaire !"}
              </div>
              <div className="text-[11px] text-gray-400 mt-0.5">
                Souhaitez un joyeux anniversaire à vos camarades
              </div>
            </div>
            <ChevronRight size={16} className="text-gray-500" />
          </button>
        )}

        {/* Banner CRITIQUE : question de sécurité non configurée */}
        {me.securite && !me.securite.question_definie && (
          <button
            onClick={() => navigate('/portail/reglages')}
            className="w-full text-left bg-orange-900/30 border-2 border-orange-700 rounded-xl p-4 hover:bg-orange-900/40 transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-700/40 border border-orange-600 flex items-center justify-center">
                <ShieldAlert size={20} className="text-orange-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-orange-200">
                  Sécurisez votre compte
                </div>
                <p className="text-xs text-orange-300 mt-1 leading-relaxed">
                  Votre compte n'est protégé que par votre <strong>date de naissance</strong>,
                  facile à connaître. Configurez une <strong>question secrète</strong> en moins
                  d'1 minute pour empêcher quiconque d'accéder à votre compte.
                </p>
                <div className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 bg-orange-700 hover:bg-orange-600 text-white text-xs font-semibold rounded">
                  Configurer maintenant →
                </div>
              </div>
            </div>
          </button>
        )}

        {/* Banner activation notifications (sobre, dismissable) */}
        {push.supported && !push.enabled && push.permission !== 'denied' && !bannerHidden && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Bell size={18} className="text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-gray-100">Activer les notifications</div>
                <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                  Soyez prévenu(e) des annonces, des résultats publiés, des emplois du temps
                  et des réponses de la scolarité, même quand l'application est fermée.
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={() => push.enable()}
                    disabled={push.loading}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium rounded transition-colors"
                  >
                    {push.loading ? 'Activation...' : 'Activer'}
                  </button>
                  <button
                    onClick={() => {
                      localStorage.setItem(PUSH_DISMISS_KEY, '1');
                      setBannerHidden(true);
                    }}
                    className="px-3 py-1.5 text-gray-400 hover:text-gray-200 text-xs transition-colors"
                  >
                    Plus tard
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bloc accès résultats */}
        {acces.autorise ? (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <div className="flex items-center gap-2 text-green-400 mb-1">
              <Unlock size={18} />
              <h3 className="font-semibold">Accès aux résultats autorisé</h3>
            </div>
            <p className="text-sm text-gray-400 mb-3">
              {acces.mode === 'force_admin'
                ? "Votre accès a été activé manuellement par la scolarité."
                : "Vous êtes à jour de vos paiements."}
            </p>
            <button
              onClick={() => navigate('/portail/releve')}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              <FileText size={16} />
              Consulter mon relevé
              <ChevronRight size={16} />
            </button>
          </div>
        ) : (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <div className="flex items-center gap-2 text-orange-400 mb-1">
              <Lock size={18} />
              <h3 className="font-semibold">Accès suspendu</h3>
            </div>
            <p className="text-sm text-gray-400">
              {acces.acces_leger ? (
                "Vous pouvez consulter votre décision (semestre validé ou non). Vos notes et moyennes restent masquées jusqu'à la régularisation de vos paiements."
              ) : (
                <>
                  {acces.mode === 'bloque_dette' && "Vous devez régulariser vos paiements pour consulter vos résultats."}
                  {acces.mode === 'bloque_absent' && "Vos informations de paiement ne sont pas encore enregistrées."}
                </>
              )}
            </p>
            {acces.acces_leger && (
              <button
                onClick={() => navigate('/portail/releve')}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                <FileText size={16} />
                Voir si j'ai validé mon semestre
                <ChevronRight size={16} />
              </button>
            )}
            {soumissionOk ? (
              <div className="mt-3 p-3 bg-green-900/20 border border-green-800/50 rounded text-xs text-green-300">
                Demande envoyée. La caisse vous notifiera après vérification.
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-400 mt-3 italic">
                  Si vous avez déjà payé, vous pouvez nous envoyer une photo de votre reçu pour activation à distance.
                </p>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button
                    onClick={() => setShowSoumettreRecu(true)}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                  >
                    <Upload size={14} />
                    Soumettre un reçu
                  </button>
                  <button
                    onClick={() => navigate('/portail/mes-demandes')}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 text-xs font-medium rounded-lg transition-colors"
                  >
                    <Inbox size={14} />
                    Mes demandes
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Statut de tous les paiements */}
        {statutPaiements.length > 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <h3 className="font-semibold text-gray-100 text-sm mb-3 flex items-center gap-2">
              Situation des paiements
              <span className="text-xs text-gray-400 font-normal">
                ({statutPaiements.filter((p: any) => p.concernee !== false && p.est_a_jour).length}/{statutPaiements.filter((p: any) => p.concernee !== false).length} à jour)
              </span>
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {statutPaiements.map((p: any) => {
                const label = PERIODE_LABELS[p.periode] || p.periode;
                // Aménagement (2026-09-12) : période qui ne concerne pas l'étudiant
                if (p.concernee === false) {
                  return (
                    <div key={p.periode} className="p-2.5 rounded-lg border flex items-center gap-2 bg-gray-800/40 border-gray-700/60">
                      <span className="w-4 text-center text-gray-500 flex-shrink-0">–</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-400 truncate">{label}</div>
                        <div className="text-[10px] text-gray-500">Non concerné</div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={p.periode}
                    className={`p-2.5 rounded-lg border flex items-center gap-2 ${
                      p.est_a_jour
                        ? 'bg-green-900/20 border-green-800/50'
                        : 'bg-red-900/20 border-red-800/50'
                    }`}
                  >
                    {p.est_a_jour ? (
                      <Check size={16} className="text-green-400 flex-shrink-0" />
                    ) : (
                      <X size={16} className="text-red-400 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-100 truncate">{label}</div>
                      {!p.est_a_jour && parseFloat(p.solde) > 0 && (
                        <div className="text-[10px] text-red-400">Reste {formatMoney(p.solde)}</div>
                      )}
                      {!p.est_a_jour && p.present === false && (
                        <div className="text-[10px] text-red-400">Non enregistré</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── ACCÈS RAPIDES : 3 essentiels ─────────────────────────────── */}
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => navigate('/portail/messagerie')}
            className="bg-gray-900 border border-gray-800 hover:border-amber-600 rounded-xl p-3 transition-colors text-center"
          >
            <MessageSquare className="w-5 h-5 mx-auto mb-1.5 text-amber-400" />
            <div className="text-xs font-semibold text-gray-200">Messagerie</div>
            <div className="text-[10px] text-gray-500 mt-0.5">Camarades · Scolarité</div>
          </button>
          <button
            onClick={() => navigate('/portail/notifications')}
            className="bg-gray-900 border border-gray-800 hover:border-amber-600 rounded-xl p-3 transition-colors text-center relative"
          >
            <Bell className="w-5 h-5 mx-auto mb-1.5 text-amber-400" />
            <div className="text-xs font-semibold text-gray-200">Notifications</div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              {me.nb_notifications_non_lues > 0 ? `${me.nb_notifications_non_lues} non lue(s)` : 'À jour'}
            </div>
            {me.nb_notifications_non_lues > 0 && (
              <span className="absolute top-1.5 right-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-red-500 text-white rounded-full px-1">
                {me.nb_notifications_non_lues}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/portail/actualites')}
            className="bg-gray-900 border border-gray-800 hover:border-amber-600 rounded-xl p-3 transition-colors text-center relative"
          >
            <Newspaper className="w-5 h-5 mx-auto mb-1.5 text-amber-400" />
            <div className="text-xs font-semibold text-gray-200">Actualité</div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              {nbActuNonLues > 0 ? `${nbActuNonLues} nouvelle(s)` : 'Fil'}
            </div>
            {nbActuNonLues > 0 && (
              <span className="absolute top-1.5 right-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5">
                {nbActuNonLues}
              </span>
            )}
          </button>
        </div>

        {/* ── BOUTON MENU COMPLET : accès aux autres pages ─────────────── */}
        <button
          onClick={() => navigate('/portail/menu')}
          className="w-full bg-gradient-to-r from-amber-900/30 to-amber-900/10 border border-amber-700/40 hover:border-amber-500 rounded-xl p-3 transition-colors flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center text-white shadow-sm">
            <Settings size={18} />
          </div>
          <div className="flex-1 text-left">
            <div className="text-sm font-bold text-gray-100">Menu complet</div>
            <div className="text-[11px] text-gray-400">
              Mes cours · EDT · Ma salle · Sujets · Réclamations · Réglages…
            </div>
          </div>
          <ChevronRight size={16} className="text-amber-400" />
        </button>
        {/* Déconnexion : disponible dans /portail/menu, retirée d'ici pour
            désaturer l'accueil (demande 2026-08-12). */}
      </div>

      {showSoumettreRecu && (
        <SoumettreRecuModal
          onClose={() => setShowSoumettreRecu(false)}
          onSubmitted={() => {
            setShowSoumettreRecu(false);
            setSoumissionOk(true);
          }}
        />
      )}

      {showAnnonceParcours && (
        <AnnonceParcoursModal onClose={fermerAnnonceParcours} />
      )}
    </PortailLayout>
  );
}

// ─── Carte profil avec upload photo ───────────────────────────────

const ProfilCard: React.FC<{
  etu: MeResponse['etudiant'];
  onPhotoChanged: (url: string | null) => void;
}> = ({ etu, onPhotoChanged }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showActions, setShowActions] = useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setError(null);
    setUploading(true);
    try {
      const res = await portailApi.uploadPhotoProfil(file);
      onPhotoChanged(res.photo_url);
      setShowActions(false);
    } catch (e: any) {
      setError(e?.message || 'Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer votre photo de profil ?')) return;
    setUploading(true);
    setError(null);
    try {
      const res = await portailApi.deletePhotoProfil();
      onPhotoChanged(res.photo_url);
      setShowActions(false);
    } catch (e: any) {
      setError(e?.message || 'Erreur');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <div className="flex items-center gap-4">
        <div className="relative">
          {etu.photo_url ? (
            <img
              src={etu.photo_url}
              alt={etu.nom}
              className="w-16 h-16 rounded-full object-cover border-2 border-gray-700"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl font-bold">
              {etu.prenoms[0]}{etu.nom[0]}
            </div>
          )}
          <button
            onClick={() => setShowActions(!showActions)}
            className="absolute -bottom-1 -right-1 w-7 h-7 bg-gray-800 hover:bg-blue-600 border-2 border-gray-900 rounded-full flex items-center justify-center text-gray-300 hover:text-white transition-colors"
            title="Modifier ma photo"
            disabled={uploading}
          >
            <Camera size={13} />
          </button>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-base leading-tight truncate text-gray-100">
            {etu.nom} {etu.prenoms}
          </div>
          <div className="text-sm text-gray-400 mt-0.5 font-mono">{etu.matricule}</div>
          <div className="text-xs text-gray-400 mt-1 truncate">
            {etu.classe || '—'}{etu.option ? ` • ${etu.option}` : ''}
          </div>
        </div>
      </div>

      {showActions && (
        <div className="mt-3 pt-3 border-t border-gray-800 flex items-center gap-2 flex-wrap">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = '';
            }}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium rounded transition-colors"
          >
            <Upload size={12} />
            {uploading ? 'Envoi…' : (etu.photo_url ? 'Changer la photo' : 'Choisir une photo')}
          </button>
          {etu.photo_url && (
            <button
              onClick={handleDelete}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-red-900/40 disabled:opacity-50 text-gray-300 hover:text-red-400 text-xs font-medium rounded transition-colors"
            >
              <Trash2 size={12} />
              Supprimer
            </button>
          )}
          <span className="text-[10px] text-gray-500 ml-auto">JPG, PNG ou WEBP — max 5 MB</span>
        </div>
      )}
      {error && (
        <div className="mt-2 text-xs text-red-400">{error}</div>
      )}
    </div>
  );
};
